import React, { useCallback, useContext, useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { HugeiconsIcon } from '@hugeicons/react'
import { Cancel01Icon } from '@hugeicons/core-solid-rounded'
import { useLingui } from '@lingui/react/macro'

import { useCloseModal } from '@/Router'
import { DirtyContext, DirtyProvider } from '@/features/form'
import ConfirmDialog from '@/components/ui/ConfirmDialog'

export const dialogContainer = document.getElementById('dialogs')

interface DialogHeaderProps {
  description?: string | React.ReactNode
  title?: string
  close?: () => void
}

interface DialogLayoutProps {
  isOpen: boolean
}

export function DialogHeader({
  children,
  description,
  title,
  close
}: React.PropsWithChildren<DialogHeaderProps>) {
  const closeDialog = useCloseModal()

  return (
    <div className="flex flex-row items-start justify-between">
      <div className="flex flex-col gap-2 p-2.5">
        <Dialog.Title className="flex flex-row items-center gap-4 text-heading1 text-text">
          {title}
          {children}
        </Dialog.Title>
        <Dialog.Description className="text-regular text-text">
          {description}
        </Dialog.Description>
      </div>
      <Dialog.Close
        onClick={close ?? closeDialog}
        className="rounded-[5px] p-1 outline-primary hover:bg-primary-bright cursor-pointer"
      >
        <HugeiconsIcon icon={Cancel01Icon} className="size-5" />
      </Dialog.Close>
    </div>
  )
}

function DialogWrapper({
  children,
  isOpen
}: React.PropsWithChildren<DialogLayoutProps>) {
  const { t } = useLingui()
  const { isAnyDirty } = useContext(DirtyContext)
  const closeDialog = useCloseModal()

  const [isTryingToClose, setIsTryingToClose] = useState(false)

  const closeDialogIfPossible = useCallback(() => {
    if (isAnyDirty) {
      setIsTryingToClose(true)

      return
    }

    closeDialog()
  }, [isAnyDirty, closeDialog])

  return (
    <>
      {isTryingToClose && (
        <ConfirmDialog
          isOpen={isTryingToClose}
          message={t`Seems like you are trying to close this window, while working on something, are you sure you want to discard all your changes?`}
          title={t`Discard changes?`}
          onConfirm={closeDialog}
          onCancel={() => setIsTryingToClose(false)}
        />
      )}
      <Dialog.Root open={isOpen}>
        <Dialog.Portal container={dialogContainer}>
          <Dialog.Overlay className="fixed size-full inset-0 bg-black/30 grid place-items-center overflow-y-auto z-dialog">
            <Dialog.Content
              className="relative w-screen max-w-[800px] py-10 px-2"
              onEscapeKeyDown={closeDialogIfPossible}
              onPointerDownOutside={e => {
                const target = e.target as HTMLElement
                if (target.closest('[data-radix-select-content]')) {
                  e.preventDefault()
                  return
                }

                closeDialogIfPossible()
              }}
              onInteractOutside={e => {
                const target = e.target as HTMLElement
                if (
                  (target as HTMLElement).closest?.(
                    '[data-radix-select-content]'
                  )
                ) {
                  e.preventDefault()
                  return
                }

                closeDialogIfPossible()
              }}
            >
              <div className="overflow-hidden rounded bg-white shadow-md">
                {children}
              </div>
            </Dialog.Content>
          </Dialog.Overlay>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  )
}

export default function DialogWithDirtyProvider(
  props: React.PropsWithChildren<DialogLayoutProps>
) {
  return (
    <DirtyProvider>
      <DialogWrapper {...props} />
    </DirtyProvider>
  )
}
